export default class Dropdown {
    constructor(buttonOrSelector, opts = {}) {
        this.opts = Object.assign({
            hover: false,
            hoverOpenDelay: 100,
            hoverCloseDelay: 150,
            disableOnTouch: true
        }, opts);

        // Init all when no selector provided
        if (!buttonOrSelector) {
            Dropdown.initAll('.dropdown-toggle', this.opts);
            return;
        }

        this.btn = typeof buttonOrSelector === 'string'
            ? document.querySelector(buttonOrSelector)
            : buttonOrSelector;

        if (!this.btn) return;

        // Standard BS4 markup: .dropdown > .dropdown-toggle + .dropdown-menu
        this.root = this.btn.closest('.dropdown') || this.btn.parentElement;
        this.menu = this.root && this.root.querySelector('.dropdown-menu');
        if (!this.root || !this.menu) return;

        this._onBtnClick = this._onBtnClick.bind(this);
        this._onDocClick = this._onDocClick.bind(this);
        this._onAnyToggleClick = this._onAnyToggleClick.bind(this);
        this._hoverIn = this._hoverIn.bind(this);
        this._hoverOut = this._hoverOut.bind(this);

        // Prefer Bootstrap 4 plugin if present, otherwise fallback
        this._isBs4 = !!(window.jQuery && jQuery.fn && typeof jQuery.fn.dropdown === 'function');
        if (this._isBs4) {
            // Initialize the BS4 dropdown behavior
            jQuery(this.btn).dropdown();

            // Keep ARIA in sync using BS4 events
            jQuery(this.root)
                .on('show.bs.dropdown', () => this.btn.setAttribute('aria-expanded', 'true'))
                .on('hide.bs.dropdown', () => this.btn.setAttribute('aria-expanded', 'false'));

            // Click handling for toggle button
            this.btn.addEventListener('click', this._onBtnClick);
            // Close if clicking anywhere outside OR on any .dropdown-toggle (requirement)
            document.addEventListener('click', this._onDocClick);
        } else {
            // Lightweight fallback
            this._enableFallback();
        }

        // Optional hover behavior
        this._enableHoverIfRequested();
    }

    // ---------- Public API ----------
    show() {
        if (this._isBs4) {
            if (!this._isOpen()) jQuery(this.btn).dropdown('toggle');
            return;
        }
        this._openFallback();
    }

    hide() {
        if (this._isBs4) {
            if (this._isOpen()) jQuery(this.btn).dropdown('toggle');
            return;
        }
        this._closeFallback();
    }

    toggle() {
        if (this._isOpen()) this.hide();
        else this.show();
    }

    // ---------- Internals ----------
    _isOpen() {
        return this.root.classList.contains('show') || this.menu.classList.contains('show');
    }

    _onBtnClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const isOpen = this._isOpen();

        // Close others first
        Dropdown.closeAll(this.root);

        // Then toggle this one
        if (this._isBs4) {
            if (!isOpen) jQuery(this.btn).dropdown('show');
            else jQuery(this.btn).dropdown('hide');
        } else {
            if (!isOpen) this._openFallback();
            else this._closeFallback();
        }
    };

    _onAnyToggleClick(e) {
        // close if a .dropdown-toggle was clicked (anywhere).
        const tgt = e.target.closest('.dropdown-toggle');
        if (!tgt) return;
        // If the click is not on our own button, close this dropdown.
        if (tgt !== this.btn) this.hide();
        // If it IS our button, BS4’s own click will handle closing if open.
    }

    _onDocClick = (e) => {
        const clickedToggle = e.target.closest('.dropdown-toggle');
        const clickedRoot = e.target.closest('.dropdown');

        // If click is outside this dropdown, close it
        if (!this.root.contains(e.target)) {
            this.hide();
            return;
        }

        // If a different dropdown's toggle was clicked, close this one
        if (clickedToggle && clickedRoot && clickedRoot !== this.root) {
            this.hide();
        }
    };

    _enableFallback() {
        this.btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const wasOpen = this._isOpen();
            Dropdown.closeAll(this.root);
            if (!wasOpen) this._openFallback();
        });
        document.addEventListener('click', (e) => {
            const toggleClicked = !!e.target.closest('.dropdown-toggle');
            if (toggleClicked) {
                // Close on ANY dropdown-toggle click (requirement)
                if (!this.root.contains(e.target)) this._closeFallback();
                return;
            }
            if (!this.root.contains(e.target)) this._closeFallback();
        });
    }

    _openFallback() {
        this.root.classList.add('show');
        this.menu.classList.add('show');
        this.btn.setAttribute('aria-expanded', 'true');

        // Simple placement below the button
        const rect = this.btn.getBoundingClientRect();
        this.menu.style.position = 'absolute';
        this.menu.style.minWidth = rect.width + 'px';
        this.menu.style.top = (this.btn.offsetTop + this.btn.offsetHeight) + 'px';
        this.menu.style.left = this.btn.offsetLeft + 'px';
        this.menu.style.zIndex = 1000;
    }

    _closeFallback() {
        this.root.classList.remove('show');
        this.menu.classList.remove('show');
        this.btn.setAttribute('aria-expanded', 'false');
    }

    _enableHoverIfRequested() {
        if (!this.opts.hover) return;
        if (this.opts.disableOnTouch && (('ontouchstart' in window) || navigator.maxTouchPoints > 0)) return;

        const enterTargets = [this.btn, this.menu];
        enterTargets.forEach(el => el.addEventListener('mouseenter', this._hoverIn));
        enterTargets.forEach(el => el.addEventListener('mouseleave', this._hoverOut));
    }

    _hoverIn() {
        clearTimeout(this._hoverCloseTimer);
        this._hoverOpenTimer = setTimeout(() => {
            Dropdown.closeAll(this.root);
            this.show();
        }, this.opts.hoverOpenDelay);
    }

    _hoverOut() {
        clearTimeout(this._hoverOpenTimer);
        this._hoverCloseTimer = setTimeout(() => {
            this.hide();
        }, this.opts.hoverCloseDelay);
    }

    destroy() {
        clearTimeout(this._hoverOpenTimer);
        clearTimeout(this._hoverCloseTimer);

        if (this._isBs4) {
            try { jQuery(this.btn).dropdown('dispose'); } catch (_) { }
            this.btn.removeEventListener('click', this._onBtnClick);
            document.removeEventListener('click', this._onDocClick);
        }
        [this.btn, this.menu].forEach(el => {
            if (!el) return;
            el.removeEventListener('mouseenter', this._hoverIn);
            el.removeEventListener('mouseleave', this._hoverOut);
        });
    }

    // ---------- Statics ----------
    static closeAll(exceptRoot = null) {
        // Close all open menus except the provided one
        document.querySelectorAll('.dropdown.show, .dropdown-menu.show').forEach(el => {
            const root = el.classList.contains('dropdown') ? el : el.closest('.dropdown');
            if (exceptRoot && root === exceptRoot) return;

            root && root.classList.remove('show');
            const menu = root && root.querySelector('.dropdown-menu');
            if (menu) menu.classList.remove('show');
            const btn = root && root.querySelector('.dropdown-toggle[aria-expanded]');
            if (btn) btn.setAttribute('aria-expanded', 'false');
        });
    }

    static initAll(selector = '.dropdown-toggle', opts = {}) {
        return Array.from(document.querySelectorAll(selector)).map(btn => new Dropdown(btn, opts));
    }
}

// Optional global
if (typeof window !== 'undefined') window.Dropdown = Dropdown;